import clsx from 'clsx';
import { Dialog, Transition } from '@headlessui/react';
import { ShoppingCartIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LoadingDots from '../loading-dots';
import Price from '../price';
import { DEFAULT_OPTION } from 'lib/constants';
import { createUrl } from '../../utils';
import Image from '../Image';
import { createCartAndSetCookie } from './actions';
import { useCart } from './cart-context';
import { useMaisonAdjiCartOptional, type MaisonAdjiCartItem } from '../../context/MaisonAdjiCartContext';
import { usePreferredCurrencyOptional } from '../../context/CurrencySettingsContext';
import { buildPayLinkCheckoutUrl, getPayLinkCheckoutType, buildMaisonAdjiPayLinkCheckoutUrl, getMaisonAdjiPayLinkCheckoutFallbackUrl } from 'lib/paylink';
import { DeleteItemButton } from './delete-item-button';
import { EditItemQuantityButton } from './edit-item-quantity-button';
import OpenCart from './open-cart';
import { supabase, type UserProfile } from '../../lib/supabase';
import { Package, MapPin, Briefcase, LogOut, User } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

type MerchandiseSearchParams = {
  [key: string]: string;
};

export default function CartModal() {
  const { t } = useLanguage();
  const { cart } = useCart();
  const maCart = useMaisonAdjiCartOptional();
  const currencySettings = usePreferredCurrencyOptional();
  const preferredCurrency = currencySettings?.preferredCurrency ?? 'XOF';
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const totalQuantity = (cart?.totalQuantity ?? 0) + (maCart?.totalQuantity ?? 0);
  const hasBurokLines = cart && cart.lines.length > 0;
  const hasMaLines = maCart && maCart.items.length > 0;
  const isEmpty = !hasBurokLines && !hasMaLines;
  const [showCheckoutGuestModal, setShowCheckoutGuestModal] = useState(false);
  const navigate = useNavigate();
  const quantityRef = useRef(cart?.totalQuantity);
  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  const getMaisonAdjiCheckoutUrl = () => {
    if (!maCart?.items?.length) return getMaisonAdjiPayLinkCheckoutFallbackUrl();
    return buildMaisonAdjiPayLinkCheckoutUrl(
      maCart.items.map((i) => ({
        productId: i.productId,
        name: i.name,
        quantity: i.quantity,
        priceXof: i.priceXof,
        priceEur: i.priceEur,
        imageUrl: i.imageUrl,
      }))
    );
  };

  const goToMaisonAdjiCheckout = () => {
    closeCart();
    setShowCheckoutGuestModal(false);
    window.location.href = getMaisonAdjiCheckoutUrl();
  };

  useEffect(() => {
    if (!cart) {
      createCartAndSetCookie();
    }
  }, [cart]);

  useEffect(() => {
    if (!user?.id) {
      setProfile(null);
      return;
    }
    supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()
      .then(({ data }) => setProfile(data ?? null));
  }, [user?.id]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      closeCart();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    if (
      totalQuantity > 0 &&
      totalQuantity !== quantityRef.current
    ) {
      if (!isOpen) {
        setIsOpen(true);
      }
      quantityRef.current = totalQuantity;
    }
  }, [isOpen, totalQuantity]);

  return (
    <>
      <button aria-label="Open cart" onClick={openCart}>
        <OpenCart quantity={totalQuantity} />
      </button>
      <Transition show={isOpen}>
        <Dialog onClose={closeCart} className="relative z-[110]">
          <Transition.Child
            as={Fragment}
            enter="transition-all ease-in-out duration-300"
            enterFrom="opacity-0 backdrop-blur-none"
            enterTo="opacity-100 backdrop-blur-[.5px]"
            leave="transition-all ease-in-out duration-200"
            leaveFrom="opacity-100 backdrop-blur-[.5px]"
            leaveTo="opacity-0 backdrop-blur-none"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition-all ease-in-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition-all ease-in-out duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="fixed bottom-0 right-0 top-0 flex h-full w-full flex-col border-l border-neutral-200 bg-white p-6 text-neutral-900 md:w-[390px]">
              {/* Section Mon compte ou Se connecter - Mobile uniquement */}
              <div className="mb-4 md:hidden">
                {user ? (
                  <UserAccountSection
                    user={user}
                    profile={profile}
                    onLinkClick={closeCart}
                    onSignOut={handleSignOut}
                    t={t}
                  />
                ) : (
                  <Link
                    to="/login"
                    onClick={closeCart}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-neutral-800 bg-white px-4 py-3 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-400 focus:ring-offset-2"
                  >
                    {t('nav.sign_in')}
                  </Link>
                )}
              </div>
              <div className="flex items-center justify-between">
                <p className="text-lg font-semibold text-neutral-900">{t('cart.title')}</p>
                <button aria-label="Close cart" onClick={closeCart}>
                  <CloseCart />
                </button>
              </div>

              {isEmpty ? (
                <div className="mt-20 flex w-full flex-col items-center justify-center overflow-hidden">
                  <ShoppingCartIcon className="h-16 text-neutral-400" />
                  <p className="mt-6 text-center text-2xl font-bold text-neutral-900">
                    {t('cart.empty')}
                  </p>
                </div>
              ) : (
                <div className="flex h-full flex-col justify-between overflow-hidden p-1">
                  <ul className="grow overflow-auto py-4">
                    {hasBurokLines && cart.lines
                      .sort((a, b) =>
                        a.merchandise.product.title.localeCompare(
                          b.merchandise.product.title
                        )
                      )
                      .map((item, i) => {
                        const merchandiseSearchParams =
                          {} as MerchandiseSearchParams;

                        item.merchandise.selectedOptions.forEach(
                          ({ name, value }) => {
                            if (value !== DEFAULT_OPTION) {
                              merchandiseSearchParams[name.toLowerCase()] =
                                value;
                            }
                          }
                        );

                        const merchandiseUrl = createUrl(
                          `/product/${item.merchandise.product.handle}`,
                          new URLSearchParams(merchandiseSearchParams)
                        );

                        return (
                          <li
                            key={i}
                            className="flex w-full flex-col border-b border-neutral-200"
                          >
                            <div className="relative flex w-full flex-row justify-between gap-2 py-4 pl-1">
                              <div className="flex flex-row min-w-0 flex-1">
                                <div className="relative shrink-0">
                                  <div className="absolute -left-1 -top-1 z-10">
                                    <DeleteItemButton item={item} />
                                  </div>
                                  <div className="h-16 w-16 overflow-hidden rounded-md border border-neutral-200 bg-neutral-100">
                                    <Image
                                      className="h-full w-full object-cover"
                                      width={64}
                                      height={64}
                                      alt={
                                        item.merchandise.product.featuredImage
                                          .altText ||
                                        item.merchandise.product.title
                                      }
                                      src={
                                        item.merchandise.product.featuredImage.url
                                      }
                                    />
                                  </div>
                                </div>
                                <Link
                                  to={merchandiseUrl}
                                  onClick={closeCart}
                                  className="z-30 ml-2 flex flex-row space-x-4"
                                >
                                  <div className="flex flex-1 flex-col text-base">
                                    <span className="leading-tight">
                                      {item.merchandise.product.title}
                                    </span>
                                    {item.merchandise.title !==
                                    DEFAULT_OPTION ? (
                                      <p className="text-sm text-neutral-500">
                                        {item.merchandise.title}
                                      </p>
                                    ) : null}
                                  </div>
                                </Link>
                              </div>
                                <div className="flex h-16 flex-col justify-between">
                                <Price
                                  className="flex justify-end space-y-2 text-right text-sm text-neutral-900"
                                  amount={item.cost.totalAmount.amount}
                                  currencyCode={
                                    item.cost.totalAmount.currencyCode
                                  }
                                />
                                <div className="ml-auto flex h-9 flex-row items-center rounded-full border border-neutral-200">
                                  <EditItemQuantityButton
                                    item={item}
                                    type="minus"
                                  />
                                  <p className="w-6 text-center">
                                    <span className="w-full text-sm">
                                      {item.quantity}
                                    </span>
                                  </p>
                                  <EditItemQuantityButton
                                    item={item}
                                    type="plus"
                                  />
                                </div>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    {hasMaLines && maCart.items.map((item: MaisonAdjiCartItem) => (
                      <li
                        key={item.productId}
                        className="flex w-full flex-col border-b border-neutral-200"
                      >
                        <div className="relative flex w-full flex-row justify-between gap-2 py-4 pl-1">
                          <div className="flex flex-row min-w-0 flex-1">
                            <div className="relative shrink-0">
                              <button
                                type="button"
                                aria-label={t('cart.remove_item')}
                                onClick={() => maCart.removeItem(item.productId)}
                                className="absolute -left-1 -top-1 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-700 shadow-md hover:bg-neutral-100 hover:text-neutral-900"
                              >
                                <XMarkIcon className="h-3.5 w-3.5" />
                              </button>
                              <div className="h-16 w-16 overflow-hidden rounded-md border border-neutral-200 bg-neutral-100">
                                <img
                                  src={item.imageUrl}
                                  alt={item.name}
                                  className="h-full w-full object-cover"
                                  width={64}
                                  height={64}
                                />
                              </div>
                            </div>
                            <Link
                              to={`/product/${item.slug}`}
                              onClick={closeCart}
                              className="z-30 ml-2 flex flex-row space-x-4"
                            >
                              <div className="flex flex-1 flex-col text-base">
                                <span className="leading-tight">{item.name}</span>
                              </div>
                            </Link>
                          </div>
                            <div className="flex h-16 flex-col justify-between">
                            <p className="text-right text-sm text-neutral-900">
                              {(preferredCurrency === 'XOF'
                                ? (item.priceXof ?? item.priceEur ?? 0)
                                : (item.priceEur ?? item.priceXof ?? 0)
                              ).toLocaleString('fr-FR')}{' '}
                              {preferredCurrency === 'XOF'
                                ? (item.priceXof != null ? 'XOF' : 'EUR')
                                : (item.priceEur != null ? 'EUR' : 'XOF')}
                            </p>
                            <div className="ml-auto flex h-9 flex-row items-center rounded-full border border-neutral-200">
                              <button
                                type="button"
                                onClick={() => maCart.updateQuantity(item.productId, item.quantity - 1)}
                                className="flex h-full w-8 items-center justify-center rounded-l-full hover:bg-neutral-100 text-neutral-700"
                                aria-label={t('cart.decrease')}
                              >
                                −
                              </button>
                              <p className="w-6 text-center text-sm text-neutral-900">{item.quantity}</p>
                              <button
                                type="button"
                                onClick={() => maCart.updateQuantity(item.productId, item.quantity + 1)}
                                className="flex h-full w-8 items-center justify-center rounded-r-full hover:bg-neutral-100 text-neutral-700"
                                aria-label={t('cart.increase')}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="py-4 text-sm text-neutral-600">
                    {hasBurokLines && (
                      <>
                        <div className="mb-3 flex items-center justify-between border-b border-neutral-200 pb-1">
                          <p className="text-neutral-600">{t('cart.taxes')}</p>
                          <Price
                            className="text-right text-base text-neutral-900"
                            amount={cart.cost.totalTaxAmount.amount}
                            currencyCode={cart.cost.totalTaxAmount.currencyCode}
                          />
                        </div>
                        <div className="mb-3 flex items-center justify-between border-b border-neutral-200 pb-1 pt-1">
                          <p className="text-neutral-600">{t('cart.shipping')}</p>
                          <p className="text-right text-neutral-600">{t('cart.shipping_calc')}</p>
                        </div>
                        <div className="mb-3 flex items-center justify-between border-b border-neutral-200 pb-1 pt-1">
                          <p className="text-neutral-600">{t('cart.total')}</p>
                          <Price
                            className="text-right text-base font-medium text-neutral-900"
                            amount={cart.cost.totalAmount.amount}
                            currencyCode={cart.cost.totalAmount.currencyCode}
                          />
                        </div>
                        <CheckoutButton t={t} />
                      </>
                    )}
                    {hasMaLines && (
                      <div className="mt-2 border-t border-neutral-200 pt-3">
                        <p className="mb-1 text-xs uppercase text-neutral-500">Maison Adji</p>
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-neutral-600">{t('cart.subtotal')}</p>
                          <p className="text-right text-base font-medium text-neutral-900">
                            {maCart.items
                              .reduce(
                                (sum, i) =>
                                  sum +
                                  (preferredCurrency === 'XOF'
                                    ? (i.priceXof ?? i.priceEur ?? 0)
                                    : (i.priceEur ?? i.priceXof ?? 0)) *
                                    i.quantity,
                                0
                              )
                              .toLocaleString('fr-FR')}{' '}
                            {preferredCurrency}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (user) {
                              closeCart();
                              window.location.href = getMaisonAdjiCheckoutUrl();
                            } else {
                              setShowCheckoutGuestModal(true);
                            }
                          }}
                          className="flex w-full items-center justify-center gap-1.5 rounded-full bg-neutral-900 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-neutral-800"
                        >
                          <span>{t('cart.checkout_with')}</span>
                          <img
                            src="/assets/images/PayLink2.png"
                            alt="PayLink"
                            className="h-6 w-auto object-contain"
                          />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>

      {/* Modal : Se connecter pour suivre la commande ou continuer en invité — au-dessus du panier (z > 110) */}
      <Transition show={showCheckoutGuestModal}>
        <Dialog
          onClose={() => setShowCheckoutGuestModal(false)}
          className="relative z-[120]"
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
                <Dialog.Title className="text-lg font-semibold text-neutral-900">
                  {t('cart.checkout_guest_modal_title')}
                </Dialog.Title>
                <p className="mt-2 text-sm text-neutral-600">
                  {t('cart.checkout_guest_modal_message')}
                </p>
                <div className="mt-6 flex flex-col gap-3">
                  <Link
                    to="/login"
                    onClick={() => {
                      setShowCheckoutGuestModal(false);
                      closeCart();
                    }}
                    className="flex w-full items-center justify-center rounded-full border-2 border-neutral-800 bg-white px-4 py-3 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-50"
                  >
                    {t('nav.sign_in')}
                  </Link>
                  <button
                    type="button"
                    onClick={goToMaisonAdjiCheckout}
                    className="w-full rounded-full bg-neutral-900 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-neutral-800"
                  >
                    {t('cart.checkout_guest_continue')}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCheckoutGuestModal(false)}
                  className="mt-4 w-full text-center text-sm text-neutral-500 hover:text-neutral-700"
                >
                  {t('cart.checkout_guest_cancel')}
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

function UserAccountSection({
  user,
  profile,
  onLinkClick,
  onSignOut,
  t,
}: {
  user: any;
  profile: UserProfile | null;
  onLinkClick: () => void;
  onSignOut: () => void;
  t: (key: string) => string;
}) {
  const firstName = profile?.first_name || user.email?.split('@')[0] || t('account.user');
  const avatarUrl = profile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(firstName)}&background=ff0442&color=fff&size=128`;
  const username = profile?.username || user.id;

  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50">
      <div className="px-4 py-3 border-b border-neutral-200">
        <Link
          to={`/user/${username}`}
          onClick={onLinkClick}
          className="flex items-center gap-3"
        >
          <img
            src={avatarUrl}
            alt={firstName}
            className="h-10 w-10 rounded-full object-cover ring-2 ring-neutral-200"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-neutral-900 truncate">
              {firstName} {profile?.last_name || ''}
            </p>
            <p className="text-xs text-neutral-500 truncate">
              {user.email}
            </p>
          </div>
          <User className="h-5 w-5 text-neutral-400" />
        </Link>
      </div>

      <div className="p-2">
        <Link
          to="/mes-commandes"
          onClick={onLinkClick}
          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors hover:bg-neutral-100 text-neutral-800"
        >
          <Package className="h-5 w-5 text-neutral-600" />
          <span className="text-sm font-medium">{t('account.my_orders')}</span>
        </Link>

        <Link
          to="/mon-adresse"
          onClick={onLinkClick}
          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors hover:bg-neutral-100 text-neutral-800"
        >
          <MapPin className="h-5 w-5 text-neutral-600" />
          <span className="text-sm font-medium">{t('account.my_address')}</span>
        </Link>

        <Link
          to="/espace-business"
          onClick={onLinkClick}
          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors hover:bg-neutral-100 text-neutral-800"
        >
          <Briefcase className="h-5 w-5 text-neutral-600" />
          <span className="text-sm font-medium">{t('account.business')}</span>
        </Link>

        <div className="border-t border-neutral-200 my-1" />

        <button
          onClick={onSignOut}
          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors hover:bg-red-50 text-red-600"
        >
          <LogOut className="h-5 w-5 text-red-600" />
          <span className="text-sm font-medium">{t('account.sign_out')}</span>
        </button>
      </div>
    </div>
  );
}

function CloseCart({ className }: { className?: string }) {
  return (
    <div className="relative flex h-11 w-11 items-center justify-center rounded-md border border-neutral-300 text-neutral-800 transition-colors hover:bg-neutral-100">
      <XMarkIcon
        className={clsx(
          'h-6 transition-all ease-in-out hover:scale-110',
          className
        )}
      />
    </div>
  );
}

function CheckoutButton({ t }: { t: (key: string) => string }) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { cart } = useCart();
  const checkoutType = getPayLinkCheckoutType(cart);

  const handleCheckout = async () => {
    setLoading(true);
    if (cart) {
      const payLinkUrl = buildPayLinkCheckoutUrl(cart, 0);
      if (payLinkUrl) {
        window.location.href = payLinkUrl;
        return;
      }
    }
    navigate('/checkout');
    setLoading(false);
  };

  const buttonLabel =
    checkoutType === 'multi' ? t('cart.checkout_multi') : t('cart.checkout_btn');

  return (
    <button
      className="block w-full rounded-full bg-neutral-900 p-3 text-center text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-50 transition-all"
      onClick={handleCheckout}
      disabled={loading}
    >
      {loading ? <LoadingDots className="bg-white" /> : buttonLabel}
    </button>
  );
}



